import { formatDate, replaceDateSnakeCaseToDash } from 'shared/utils/date'

import {
  DeliverySlotData,
  DeliverySlotsByTimeOfTheDay,
  TMSDeliverySlotData,
  TMSDeliverySlotReservationPayload,
  TMSDeliverySlotsArrayItem,
  TMSDeliverySlotsArraySlot
} from './types'

const CUT_OFF_HOUR = 18

export const getTimeInMinutes = (timeStr: string | undefined): number => {
  if (!timeStr) return 0

  const startTime = timeStr.split('-')[0]
  const [hours, minutes] = startTime.split(':').map(Number)

  return hours * 60 + minutes
}

export const divideTimeSlotsByTimeOfTheDay = (
  timeSlots: TMSDeliverySlotsArraySlot[]
): {
  morning: TMSDeliverySlotsArraySlot[]
  afternoon: TMSDeliverySlotsArraySlot[]
  evening: TMSDeliverySlotsArraySlot[]
} => {
  const morning: TMSDeliverySlotsArraySlot[] = []
  const afternoon: TMSDeliverySlotsArraySlot[] = []
  const evening: TMSDeliverySlotsArraySlot[] = []

  timeSlots.forEach(slot => {
    const timePart = slot.time.split(' - ')[0] // Get the start time, e.g., "10:00 AM"
    const timeComponents = timePart.split(':')
    let hours = parseInt(timeComponents[0], 10)
    const minutes = parseInt(timeComponents[1].substring(0, 2), 10) // Remove AM/PM and get minutes
    const period = timePart.substring(timePart.length - 2) // Get AM or PM

    if (period === 'PM' && hours !== 12) {
      hours += 12 // Convert to 24-hour format
    } else if (period === 'AM' && hours === 12) {
      hours = 0 // Midnight case
    }

    const timeInMinutes = hours * 60 + minutes

    if (timeInMinutes >= 4 * 60 && timeInMinutes < 12 * 60) {
      morning.push(slot)
    } else if (timeInMinutes >= 12 * 60 && timeInMinutes < 18 * 60) {
      afternoon.push(slot)
    } else {
      evening.push(slot)
    }
  })

  return {
    morning,
    afternoon,
    evening
  }
}

export const generateEmptyDeliverySlots = (
  date: string
): DeliverySlotsByTimeOfTheDay => {
  const slots: TMSDeliverySlotsArraySlot[] = []

  const dayName = new Date(date).toLocaleDateString('pl-PL', {
    weekday: 'long'
  })

  for (let hour = 6; hour < 23; hour++) {
    for (let minute = 0; minute < 60; minute += 30) {
      const startHour = hour
      const startMinute = minute
      const endHour = hour + 1
      const endMinute = minute

      const startTime = `${startHour.toString().padStart(2, '0')}:${startMinute
        .toString()
        .padStart(2, '0')}`
      const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute
        .toString()
        .padStart(2, '0')}`

      slots.push({
        date,
        time: `${startTime}-${endTime}`,
        dayName
      })
    }
  }

  return divideTimeSlotsByTimeOfTheDay(slots)
}

export const generateNext7Days = (): {
  date: string
  dayOfWeek: string
  shortDate: string
}[] => {
  const slots: { date: string; dayOfWeek: string; shortDate: string }[] = []

  for (let i = 0; i < 7; i++) {
    const today: Date = new Date()
    const firstDeliveryAvailableDate =
      today.getHours() <= CUT_OFF_HOUR
        ? today.getDate() + 1
        : today.getDate() + 2

    today.setDate(firstDeliveryAvailableDate + i)
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')

    slots.push({
      date: `${year}-${month}-${day}`,
      dayOfWeek: today.toLocaleDateString('pl-PL', { weekday: 'long' }),
      shortDate: `${day} ${today.toLocaleDateString('pl-PL', {
        month: 'short'
      })}`
    })
  }

  return slots
}

export const checkIfCanSelectSlot = (
  selectedSlots: TMSDeliverySlotsArraySlot[],
  slot?: TMSDeliverySlotsArraySlot
): boolean => {
  if (!slot) return false

  if (selectedSlots.some(selected => selected.time === slot.time)) return true

  if (selectedSlots.length === 0) return true

  if (selectedSlots.length >= 7) return false

  const selectedTimes = selectedSlots.map(s => getTimeInMinutes(s.time))
  const earliestTime = Math.min(...selectedTimes)
  const latestTime = Math.max(...selectedTimes)

  const windowStart = earliestTime - 240 // 4 hours before earliest
  const windowEnd = latestTime + 240 // 4 hours after latest

  const newSlotTime = getTimeInMinutes(slot.time)

  if (newSlotTime < windowStart || newSlotTime > windowEnd) {
    return false
  }

  const newEarliestTime = Math.min(earliestTime, newSlotTime)
  const newLatestTime = Math.max(latestTime, newSlotTime)

  if (newLatestTime - newEarliestTime > 180) {
    return false
  }

  return true
}

export const formatDeliveryExtendedData = (
  start?: string,
  end?: string
): string => {
  if (!start || !end) return ''

  const startDate = new Date(start)
  const endDate = new Date(end)
  const startDateHourFormatted = startDate.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit'
  })
  const endDateHourFormatted = endDate.toLocaleTimeString('pl-PL', {
    hour: '2-digit',
    minute: '2-digit'
  })

  return `${startDateHourFormatted} - ${endDateHourFormatted}`
}

const filterUndefinedSlots = (
  selectedSlots: Array<TMSDeliverySlotsArraySlot | undefined>
): TMSDeliverySlotsArraySlot[] => {
  return !selectedSlots.every(slot => typeof slot !== 'undefined')
    ? selectedSlots.filter(slot => typeof slot !== 'undefined')
    : selectedSlots
}

export const mapSelectedSlotsToPayload = (
  selectedSlots?: (TMSDeliverySlotsArraySlot | undefined)[]
): TMSDeliverySlotReservationPayload | null => {
  if (selectedSlots?.length === 0 || typeof selectedSlots === 'undefined') {
    return null
  }

  const filteredSelectedSlots = filterUndefinedSlots(selectedSlots)

  filteredSelectedSlots.sort(
    (a, b) =>
      new Date(a?.from?.toString() || '').getTime() -
      new Date(b?.from?.toString() || '').getTime()
  )

  const earliestTime = filteredSelectedSlots[0]?.from
  const latestTime = filteredSelectedSlots[filteredSelectedSlots.length - 1]?.to

  if (!earliestTime || !latestTime || !filteredSelectedSlots[0]) {
    return null
  }

  return {
    extended: {
      from: earliestTime,
      to: latestTime
    },
    slot: filteredSelectedSlots[0]
  }
}

export const getIntermediateSlots = (
  slot1: TMSDeliverySlotsArraySlot | undefined,
  slot2: TMSDeliverySlotsArraySlot | undefined,
  allSlots: TMSDeliverySlotsArraySlot[]
): TMSDeliverySlotsArraySlot[] => {
  if (!slot1 || !slot2) return []
  const time1 = getTimeInMinutes(slot1.time)
  const time2 = getTimeInMinutes(slot2.time)

  const earlierTime = Math.min(time1, time2)
  const laterTime = Math.max(time1, time2)

  return allSlots.filter(slot => {
    const slotTime = getTimeInMinutes(slot.time)

    return slotTime > earlierTime && slotTime < laterTime
  })
}

export const getAffectedSlots = (
  slot: TMSDeliverySlotsArraySlot,
  action: 'add' | 'remove',
  deliverySlots: DeliverySlotsByTimeOfTheDay | null,
  emptyDeliverySlots: DeliverySlotsByTimeOfTheDay,
  selectedDeliverySlots: TMSDeliverySlotsArraySlot[]
): TMSDeliverySlotsArraySlot[] => {
  if (action === 'add') {
    if (selectedDeliverySlots.length === 0) return [slot]

    const allSlots = [
      ...(emptyDeliverySlots?.morning || []),
      ...(emptyDeliverySlots?.afternoon || []),
      ...(emptyDeliverySlots?.evening || [])
    ]

    const availableSlots = [
      ...(deliverySlots?.morning || []),
      ...(deliverySlots?.afternoon || []),
      ...(deliverySlots?.evening || [])
    ]

    const slotTime = getTimeInMinutes(slot.time)
    const selectedTimes = selectedDeliverySlots.map(s => ({
      slot: s,
      time: getTimeInMinutes(s.time)
    }))

    selectedTimes.sort((a, b) => a.time - b.time)

    let insertIndex = selectedTimes.findIndex(s => s.time > slotTime)

    if (insertIndex === -1) insertIndex = selectedTimes.length

    const prevSlot = selectedTimes[insertIndex - 1]?.slot
    const nextSlot = selectedTimes[insertIndex]?.slot

    let affectedSlots = [slot]

    if (prevSlot) {
      const intermediateSlots = getIntermediateSlots(prevSlot, slot, allSlots)

      affectedSlots = [
        ...affectedSlots,
        ...intermediateSlots.filter(intermediateSlot =>
          availableSlots.some(
            availableSlot => availableSlot.time === intermediateSlot.time
          )
        )
      ]
    }

    if (nextSlot) {
      const intermediateSlots = getIntermediateSlots(slot, nextSlot, allSlots)

      affectedSlots = [
        ...affectedSlots,
        ...intermediateSlots.filter(intermediateSlot =>
          availableSlots.some(
            availableSlot => availableSlot.time === intermediateSlot.time
          )
        )
      ]
    }

    return affectedSlots.filter(
      (affectedSlot, index, self) =>
        index === self.findIndex(s => s.time === affectedSlot.time)
    )
  } else {
    const deselectedTime = getTimeInMinutes(slot.time)
    const slotsBeforeCount = selectedDeliverySlots.filter(
      selectedSlot => getTimeInMinutes(selectedSlot.time) < deselectedTime
    ).length
    const slotsAfterCount = selectedDeliverySlots.filter(
      selectedSlot => getTimeInMinutes(selectedSlot.time) > deselectedTime
    ).length

    if (slotsBeforeCount <= slotsAfterCount) {
      return selectedDeliverySlots.filter(
        selectedSlot => getTimeInMinutes(selectedSlot.time) <= deselectedTime
      )
    } else {
      return selectedDeliverySlots.filter(
        selectedSlot => getTimeInMinutes(selectedSlot.time) >= deselectedTime
      )
    }
  }
}

/**
 * Get affected slots when hovering over a slot
 */
export const getAffectedSlotsCallback = (
  slot: TMSDeliverySlotsArraySlot,
  action: 'add' | 'remove',
  deliverySlots: DeliverySlotsByTimeOfTheDay | null,
  emptyDeliverySlots: DeliverySlotsByTimeOfTheDay | null,
  selectedDeliverySlots: TMSDeliverySlotsArraySlot[]
): TMSDeliverySlotsArraySlot[] => {
  if (!emptyDeliverySlots) return []

  return getAffectedSlots(
    slot,
    action,
    deliverySlots,
    emptyDeliverySlots,
    selectedDeliverySlots
  )
}

export const handleAddSlot =
  (
    slot: TMSDeliverySlotsArraySlot,
    deliverySlots: DeliverySlotsByTimeOfTheDay | null,
    emptyDeliverySlots: DeliverySlotsByTimeOfTheDay | null
  ) =>
  (
    selectedDeliverySlots: TMSDeliverySlotsArraySlot[]
  ): TMSDeliverySlotsArraySlot[] => {
    if (checkIfCanSelectSlot(selectedDeliverySlots, slot)) {
      if (selectedDeliverySlots.length === 0) {
        return [slot]
      } else {
        const allSlots = [
          ...(emptyDeliverySlots?.morning || []),
          ...(emptyDeliverySlots?.afternoon || []),
          ...(emptyDeliverySlots?.evening || [])
        ]

        const availableSlots = [
          ...(deliverySlots?.morning || []),
          ...(deliverySlots?.afternoon || []),
          ...(deliverySlots?.evening || [])
        ]

        const slotTime = getTimeInMinutes(slot.time)
        const selectedTimes = selectedDeliverySlots.map(s => ({
          slot: s,
          time: getTimeInMinutes(s.time)
        }))

        selectedTimes.sort((a, b) => a.time - b.time)

        let insertIndex = selectedTimes.findIndex(s => s.time > slotTime)

        if (insertIndex === -1) insertIndex = selectedTimes.length

        const prevSlot = selectedTimes[insertIndex - 1]?.slot
        const nextSlot = selectedTimes[insertIndex]?.slot

        let slotsToAdd = [slot]

        if (prevSlot) {
          const intermediateSlots = getIntermediateSlots(
            prevSlot,
            slot,
            allSlots
          )

          slotsToAdd = [
            ...slotsToAdd,
            ...intermediateSlots.filter(intermediateSlot =>
              availableSlots.some(
                availableSlot => availableSlot.time === intermediateSlot.time
              )
            )
          ]
        }

        if (nextSlot) {
          const intermediateSlots = getIntermediateSlots(
            slot,
            nextSlot,
            allSlots
          )

          slotsToAdd = [
            ...slotsToAdd,
            ...intermediateSlots.filter(intermediateSlot =>
              availableSlots.some(
                availableSlot => availableSlot.time === intermediateSlot.time
              )
            )
          ]
        }

        const newSelection = [...selectedDeliverySlots, ...slotsToAdd].filter(
          (selectionSlot, index, self) =>
            index === self.findIndex(s => s?.time === selectionSlot?.time)
        )

        return newSelection
      }
    }

    return selectedDeliverySlots
  }

export const handleRemoveSlot =
  (slot: TMSDeliverySlotsArraySlot) =>
  (
    selectedDeliverySlots: TMSDeliverySlotsArraySlot[]
  ): TMSDeliverySlotsArraySlot[] => {
    const deselectedTime = getTimeInMinutes(slot.time)

    const slotsBeforeCount = selectedDeliverySlots.filter(
      selectedSlot => getTimeInMinutes(selectedSlot.time) < deselectedTime
    ).length

    const slotsAfterCount = selectedDeliverySlots.filter(
      selectedSlot => getTimeInMinutes(selectedSlot.time) > deselectedTime
    ).length

    if (slotsBeforeCount <= slotsAfterCount) {
      const updatedSlots = selectedDeliverySlots.filter(selectedSlot => {
        const selectedTime = getTimeInMinutes(selectedSlot.time)

        return selectedTime > deselectedTime
      })

      return updatedSlots
    } else {
      const updatedSlots = selectedDeliverySlots.filter(selectedSlot => {
        const selectedTime = getTimeInMinutes(selectedSlot.time)

        return selectedTime < deselectedTime
      })

      return updatedSlots
    }
  }

export const timesOfTheDaySectionTitles = {
  morning: 'Godziny poranne',
  afternoon: 'Godziny popołudniowe',
  evening: 'Godziny wieczorne'
}

export const formatDateWithWeekday = (date: Date): string => {
  const formattedDate = date.toLocaleDateString('pl-PL')
  const weekday = date.toLocaleDateString('pl-PL', { weekday: 'long' })

  const formattedWeekday = weekday[0].toUpperCase() + weekday.slice(1)

  return `${formattedDate} (${formattedWeekday})`
}

export const formatDateWithTime = (date: Date): string => {
  const formattedDate = date.toLocaleDateString('pl-PL')
  const formattedTime = date.toLocaleTimeString('pl-PL')

  return `${formattedDate} godz.${formattedTime}`
}

export const formatPhoneNumber = (phoneNumber: string): string => {
  if (!phoneNumber) return ''

  return phoneNumber.replace(/(\d{3})(\d{3})(\d{3})/, '$1-$2-$3')
}

export interface CalendarAvailbility {
  date: Date
  value: boolean
}

interface MapAvailableSlotToCalendarAvailbilityProps {
  deliverySlots: DeliverySlotData
  displayedSlots?: number
  calendarAvailbility: CalendarAvailbility[]
}

export const mapAvailableSlotToCalendarAvailbility = ({
  deliverySlots,
  displayedSlots = 3,
  calendarAvailbility
}: MapAvailableSlotToCalendarAvailbilityProps) => {
  return Object.keys(deliverySlots)
    .slice(0, displayedSlots)
    .map(dateKey => {
      const dateString = replaceDateSnakeCaseToDash(dateKey)

      const date = new Date(dateString)
      const dayName = date.toLocaleDateString('pl-PL', {
        weekday: 'short'
      })
      const formatedDate = formatDate(dateString, 'DD.MM')
      const currentSlotCalendarAvailbilityDay = calendarAvailbility.find(
        item => item.date.toISOString() === date.toISOString()
      )
      const isAvailable = currentSlotCalendarAvailbilityDay
        ? currentSlotCalendarAvailbilityDay.value
        : true

      return {
        dateKey,
        dayName,
        date: formatedDate,
        isAvailable
      }
    })
}

export const parseTMSDeliverySlots = (
  data: TMSDeliverySlotData | null
): TMSDeliverySlotsArrayItem[] => {
  const result: TMSDeliverySlotsArrayItem[] = []

  if (data) {
    for (const date in data) {
      const timeSlots: TMSDeliverySlotsArraySlot[] = []

      data[date].forEach(slot => {
        timeSlots.push({
          ...slot,
          startTime: slot.startTime,
          endTime: slot.endTime,
          time: slot.slot,
          date: slot.date,
          dayName: getDaysOfWeek(date)
        })
      })

      result.push({
        date: date,
        timeSlots: timeSlots
      })
    }
  }

  return result
}

export const getDaysOfWeek = (date: string) => {
  const dateString = replaceDateSnakeCaseToDash(date)
  const today = new Date()
  const givenDate = new Date(dateString)

  const todayMonth = today.getMonth()
  const todayDay = today.getDate()
  const givenMonth = givenDate.getMonth()
  const givenDay = givenDate.getDate()

  if (todayMonth === givenMonth && todayDay === givenDay) {
    return 'Dziś'
  }

  const days = [
    'Niedziela',
    'Poniedziałek',
    'Wtorek',
    'Środa',
    'Czwartek',
    'Piątek',
    'Sobota'
  ]
  const dayIndex = givenDate.getDay()

  return days[dayIndex]
}
